<script lang="ts" module>
  /** One fact on a dot-joined meta line. */
  export interface MetaFact {
    text: string;
    /** Renders the fact in the stronger meta colour/weight (an identity, a tag). */
    strong?: boolean;
    testId?: string;
  }

  /** Accepted so a caller can express "this fact is not known yet" inline. */
  export type MetaFactInput = MetaFact | null | undefined | false;
</script>

<script lang="ts">
  import type { Snippet } from 'svelte';

  // MetaLine — the design-system dot-joined supporting line ("#tag · 3 takes ·
  // last rehearsed 12/09"). The separator is emitted BEFORE each fact but the
  // first, so a missing or wrapping fact can never leave a trailing "·" — the
  // defect every hand-rolled copy of this line grew independently.
  interface Props {
    facts: MetaFactInput[];
    /** `span` for a line nested inside other inline content. */
    as?: 'p' | 'span';
    /** A last fact that is markup rather than text, such as a live label. */
    tail?: Snippet;
    class?: string;
    'data-testid'?: string;
  }

  const {
    facts,
    as: tag = 'p',
    class: klass = '',
    'data-testid': testId,
    tail,
  }: Props = $props();

  const shown = $derived(
    facts.filter((f): f is MetaFact => !!f && f.text.trim().length > 0),
  );
</script>

{#if shown.length > 0 || tail}
  <svelte:element this={tag} class="vd-meta-line {klass}" data-testid={testId}>
    {#each shown as fact, i (i)}
      {#if i > 0}<span class="vd-meta-line__sep" aria-hidden="true">·</span>{/if}
      <span class="vd-meta-line__fact" class:vd-meta-line__fact--strong={fact.strong} data-testid={fact.testId}>
        {fact.text}
      </span>
    {/each}
    {#if tail}
      {#if shown.length > 0}<span class="vd-meta-line__sep" aria-hidden="true">·</span>{/if}
      <span class="vd-meta-line__fact">{@render tail()}</span>
    {/if}
  </svelte:element>
{/if}

<style lang="scss">
  .vd-meta-line {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 0.4rem;
    margin: 0;
    color: var(--ink-soft);
    font-size: var(--text-meta);
    font-variant-numeric: tabular-nums;
  }

  .vd-meta-line__sep {
    color: var(--ink-mute);
  }

  .vd-meta-line__fact--strong {
    font-weight: 700;
    color: var(--ink);
  }
</style>
